import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePortalContext } from "@/lib/use-subdomain";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  InputOTP, InputOTPGroup, InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  ArrowRight, ArrowLeft, Loader2, ShieldCheck, Building2, MapPin, GraduationCap, Sparkles,
} from "lucide-react";

function startGoogleSignIn() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

// Terracotta-free primary button matching Landing's white pill CTA.
function PrimaryBtn({ children, disabled, type = "button", onClick, testid, className = "" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-white text-[#050510] font-semibold hover:bg-white/90 transition-colors w-full group disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}

// Ghost outline button for secondary actions (Google, back, etc.)
function GhostBtn({ children, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full border border-white/20 text-white hover:bg-white/[0.06] font-medium w-full transition-colors"
    >
      {children}
    </button>
  );
}

export default function Login() {
  // Stage flow: 'select' → user picks institution+campus → 'auth' → email+password
  //             OR 'platform' → super-admin mode: skip picker, direct email+password
  const [stage, setStage] = useState("select");
  const [institutionId, setInstitutionId] = useState("");
  const [campusId, setCampusId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Portal auto-detection — if we land on stpeters-kengeri.edukampus.com or
  // /p/stpeters-kengeri, we skip Stage 1 and go straight to the branded login.
  const portal = usePortalContext();

  useEffect(() => {
    if (portal.data && stage === "select") {
      setInstitutionId(portal.data.institution_id);
      setCampusId(portal.data.campus_id);
      setStage("auth");
    }
  }, [portal.data]);

  useEffect(() => {
    if (!portal.data) return;
    const { institution_name, campus_name, logo_url } = portal.data;
    document.title = `${institution_name} · ${campus_name} — Sign in`;
    if (logo_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = logo_url;
    }
  }, [portal.data]);

  const instsQ = useQuery({
    queryKey: ["public-insts"],
    queryFn: () => api.get("/public/institutions").then((r) => r.data),
    staleTime: 60_000,
  });
  const campusesQ = useQuery({
    queryKey: ["public-campuses", institutionId],
    queryFn: () => api.get(`/public/institutions/${institutionId}/campuses`).then((r) => r.data),
    enabled: !!institutionId,
    staleTime: 60_000,
  });
  const ctxQ = useQuery({
    queryKey: ["login-ctx", institutionId, campusId],
    queryFn: () => api.get("/public/login-context", { params: { institution_id: institutionId, campus_id: campusId } }).then((r) => r.data),
    enabled: stage === "auth" && !!institutionId && !!campusId,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (instsQ.data?.length && !institutionId) setInstitutionId(instsQ.data[0].id);
  }, [instsQ.data, institutionId]);
  useEffect(() => { setCampusId(""); }, [institutionId]);

  const goAfterLogin = (userObj) => {
    // Everyone lands on /dashboard by default — finance/tenants/etc. are
    // sidebar links, not the home. Bookmarks like ?from=/students still win.
    const from = location.state?.from?.pathname || "/dashboard";
    navigate(from, { replace: true });
  };

  const onContinue = (e) => {
    e?.preventDefault?.();
    setError("");
    if (!institutionId) return setError("Please pick your institution.");
    if (!campusId) return setError("Please pick your campus.");
    setStage("auth");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const extra =
        stage === "platform"
          ? {}
          : { institution_id: institutionId, campus_id: campusId };
      const res = await login(email, password, extra);
      if (res.mfa_required) setMfa({ challenge_token: res.challenge_token });
      // SEC-020: nudge privileged users through MFA setup on their first
      // successful login. Still allow entry; frontend just routes them to
      // /mfa/setup instead of the dashboard so they can turn it on.
      else if (res.mfa_setup_required) {
        try { window.sessionStorage.setItem("mfa_setup_reason", "recommended-for-role"); } catch { /* ignore */ }
        navigate("/mfa/setup", { replace: true, state: { returnTo: "/dashboard" } });
      }
      else goAfterLogin(res.user);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (e) => {
    e?.preventDefault?.();
    setError("");
    setLoading(true);
    try {
      const verifyRes = await verifyMfa(mfa.challenge_token, code);
      goAfterLogin(verifyRes?.user);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  // ----- Branding for Stage 2 -----
  const ctx = ctxQ.data;
  const selectedInst = (instsQ.data || []).find((i) => i.id === institutionId);
  const selectedCampus = (campusesQ.data || []).find((c) => c.id === campusId);
  const stage2Bg =
    portal.data?.background_url ||
    ctx?.campus?.background_url ||
    ctx?.institution?.background_url ||
    selectedCampus?.background_url ||
    selectedInst?.background_url ||
    null;
  const stage2Logo =
    portal.data?.logo_url ||
    ctx?.campus?.logo_url ||
    ctx?.institution?.logo_url ||
    selectedCampus?.logo_url ||
    selectedInst?.logo_url ||
    null;

  const errBox = (msg, tid) => (
    <div
      className="text-sm rounded-lg px-3 py-2 border border-red-500/40 bg-red-500/[0.06] text-red-300"
      data-testid={tid}
    >
      {msg}
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#050510] text-white font-sans" data-testid="login-page">
      {/* ============ LEFT BRAND PANEL ============ */}
      <div className="relative hidden lg:flex w-1/2 overflow-hidden">
        {stage === "select" || stage === "platform" ? (
          <>
            {/* Ambient background layers matching the Landing hero */}
            <div className="absolute inset-0 bg-[#050510]" />
            <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-blue-600/25 blur-3xl" />
            <div className="absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full bg-indigo-500/25 blur-3xl" />
            <div className="absolute inset-0 grid-pattern opacity-[0.06]" />

            {/* A single decorative orb — smaller cousin of Landing's hero orbs */}
            <div
              className="absolute right-[-60px] top-[30%] w-[360px] h-[360px] rounded-[46%_54%_42%_58%_/_48%_44%_56%_52%] opacity-90"
              style={{
                background: "radial-gradient(circle at 30% 30%, #6b8dff 0%, #2c4bce 55%, #0a1a5c 100%)",
                boxShadow: "0 40px 100px rgba(59,130,246,0.35), inset -18px -22px 60px rgba(0,0,0,0.4)",
              }}
            />

            <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
              <Link to="/" className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 grid place-items-center">
                  <GraduationCap className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-display font-extrabold text-2xl leading-none tracking-tight">EduKampus</div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.3em] mt-1 text-white/50">Education Operating System</div>
                </div>
              </Link>

              <div className="max-w-md space-y-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.2em] text-white/80">
                  <Sparkles className="h-3 w-3 text-blue-400" />
                  The Education Operating System
                </div>
                <h1 className="font-display font-extrabold text-[clamp(2.25rem,4.4vw,3.5rem)] leading-[0.95] tracking-[-0.03em]">
                  Run your entire school
                  <br />
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-sky-300 to-white">
                    on a single platform.
                  </span>
                </h1>
                <p className="text-sm leading-relaxed max-w-sm text-white/60">
                  Multi-tenant. K-12 + Higher Education. From admissions to accreditation.
                </p>
                <div className="flex gap-8 pt-2">
                  {[{ n: "17", l: "Campuses" }, { n: "3", l: "Institutions" }, { n: "24/7", l: "Uptime" }].map((s) => (
                    <div key={s.l} className="border-l-2 border-blue-500/60 pl-3">
                      <div className="font-display font-extrabold text-2xl">{s.n}</div>
                      <div className="text-[10px] uppercase tracking-wider mt-1 text-white/50">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/40">
                © {new Date().getFullYear()} EduKampus · Multi-Tenant SaaS
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Stage 2 — branded campus panel */}
            {stage2Bg ? (
              <img src={stage2Bg} alt={ctx?.campus?.name || "Campus"} className="absolute inset-0 h-full w-full object-cover" data-testid="stage2-bg" />
            ) : (
              <div className="absolute inset-0 bg-[#050510]" />
            )}
            <div
              className="absolute inset-0"
              style={
                portal.data?.primary_color
                  ? { background: `linear-gradient(135deg, ${portal.data.primary_color}CC 0%, rgba(5,5,16,0.9) 100%)` }
                  : { background: "linear-gradient(135deg, rgba(5,5,16,0.85) 0%, rgba(59,130,246,0.35) 55%, rgba(5,5,16,0.9) 100%)" }
              }
              data-testid="stage2-overlay"
            />
            {/* Ambient blur */}
            <div className="absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-blue-600/25 blur-3xl" />

            <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
              <div className="flex items-center gap-3">
                {stage2Logo ? (
                  <img src={stage2Logo} alt="logo" className="h-12 w-12 rounded-xl object-contain p-1 border" style={{ background: "rgba(255,255,255,0.15)", borderColor: "rgba(255,255,255,0.25)", backdropFilter: "blur(6px)" }} />
                ) : (
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center border" style={{ background: "rgba(255,255,255,0.15)", borderColor: "rgba(255,255,255,0.25)", backdropFilter: "blur(6px)" }}>
                    <GraduationCap className="h-7 w-7 text-white" />
                  </div>
                )}
                <div>
                  <div className="font-display font-extrabold text-2xl leading-none tracking-tight" data-testid="stage2-inst-name">
                    {portal.data?.institution_name || ctx?.institution?.name || selectedInst?.name || "Institution"}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.3em] mt-1 text-white/60">
                    Powered by EduKampus
                  </div>
                </div>
              </div>

              <div className="max-w-md space-y-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-blue-300">
                  <MapPin className="h-3 w-3 inline -mt-0.5 mr-1" /> Campus
                </div>
                <h1 className="font-display font-extrabold text-[clamp(2rem,4vw,3rem)] leading-[0.95] tracking-[-0.03em]" data-testid="stage2-campus-name">
                  {portal.data?.campus_name || ctx?.campus?.name || selectedCampus?.name || ""}
                </h1>
                {(ctx?.campus?.city || selectedCampus?.city) && (
                  <p className="text-sm text-white/75">{ctx?.campus?.city || selectedCampus?.city}</p>
                )}
                {portal.data?.tagline && (
                  <p className="text-sm italic border-l-2 border-blue-400 pl-3 mt-3 text-white/85" data-testid="portal-tagline">
                    {portal.data.tagline}
                  </p>
                )}
              </div>

              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/40">
                © {new Date().getFullYear()} EduKampus
              </div>
            </div>
          </>
        )}
      </div>

      {/* ============ RIGHT FORM PANEL ============ */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 overflow-y-auto bg-[#050510]">
        <div className="w-full max-w-[440px]">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 grid place-items-center">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight">EduKampus</span>
          </div>

          {mfa ? (
            // ---- MFA ----
            <form onSubmit={onVerify} className="space-y-6" data-testid="mfa-verify-form">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] mb-3 flex items-center gap-2 text-blue-400">
                  <ShieldCheck className="h-3 w-3" /> Two-factor required
                </div>
                <h2 className="font-display font-extrabold text-3xl tracking-tight">Verify it&apos;s you</h2>
                <p className="text-sm mt-2 text-white/60">
                  Open your authenticator app and enter the 6-digit code.
                </p>
              </div>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="mfa-code-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 bg-white/[0.03] border-white/10 text-white" />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {error && errBox(error, "mfa-error")}
              <PrimaryBtn type="submit" disabled={loading || code.length < 6} testid="mfa-verify-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & continue"}
              </PrimaryBtn>
              <button type="button" onClick={() => { setMfa(null); setCode(""); }} className="w-full text-xs text-white/50 hover:text-white transition-colors">
                ← Back to sign in
              </button>
            </form>
          ) : stage === "select" ? (
            <>
              <div className="mb-7">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2 flex items-center gap-2 text-blue-400">
                  <Sparkles className="h-3 w-3" /> Welcome back
                </div>
                <h2 className="font-display font-extrabold text-4xl tracking-tight">Sign in</h2>
                <p className="text-sm mt-2 text-white/60">
                  First, tell us where you belong. Or{" "}
                  <Link to="/campuses" className="text-blue-400 hover:text-blue-300 underline underline-offset-4" data-testid="login-directory-link">
                    browse the campus directory
                  </Link>.
                </p>
              </div>
              <form onSubmit={onContinue} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-[0.25em] flex items-center gap-1.5 text-white/60">
                    <Building2 className="h-3 w-3" /> Institution
                  </Label>
                  <Select value={institutionId} onValueChange={setInstitutionId}>
                    <SelectTrigger className="h-11 bg-white/[0.03] border-white/10 text-white" data-testid="login-institution-select">
                      <SelectValue placeholder={instsQ.isLoading ? "Loading…" : "Select your institution"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(instsQ.data || []).map((i) => (
                        <SelectItem key={i.id} value={i.id} data-testid={`login-inst-option-${i.id}`}>{i.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-[0.25em] flex items-center gap-1.5 text-white/60">
                    <MapPin className="h-3 w-3" /> Campus
                  </Label>
                  <Select value={campusId} onValueChange={setCampusId} disabled={!institutionId || campusesQ.isLoading}>
                    <SelectTrigger className="h-11 bg-white/[0.03] border-white/10 text-white" data-testid="login-campus-select">
                      <SelectValue placeholder={
                        !institutionId ? "Select institution first" :
                        campusesQ.isLoading ? "Loading campuses…" : "Select your campus"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {(campusesQ.data || []).map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`login-campus-option-${c.id}`}>
                          {c.name}{c.city ? ` · ${c.city}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {error && errBox(error, "login-error")}

                <PrimaryBtn type="submit" disabled={!institutionId || !campusId} testid="login-continue-button">
                  Continue <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </PrimaryBtn>

                <div className="text-[10px] font-mono uppercase tracking-[0.2em] pt-4 border-t border-white/10 text-white/50">
                  Need help? Contact your institution&apos;s IT admin
                </div>
              </form>

              <button
                type="button"
                onClick={() => {
                  setStage("platform");
                  setError("");
                  setInstitutionId("");
                  setCampusId("");
                }}
                className="mt-4 w-full text-xs flex items-center justify-center gap-1.5 py-2 rounded-full text-white/50 hover:text-white transition-colors"
                data-testid="login-platform-admin-link"
              >
                <ShieldCheck className="h-3 w-3" />
                Sign in as platform admin
              </button>
            </>
          ) : stage === "platform" ? (
            // ---- PLATFORM ADMIN (super_admin) ----
            <>
              <button
                type="button"
                onClick={() => { setStage("select"); setError(""); setEmail(""); setPassword(""); }}
                className="text-xs mb-4 flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"
                data-testid="platform-back-button"
              >
                <ArrowLeft className="h-3 w-3" /> Back to tenant sign-in
              </button>

              <div className="mb-7">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2 flex items-center gap-1.5 text-blue-400">
                  <ShieldCheck className="h-3 w-3" /> Platform Console
                </div>
                <h2 className="font-display font-extrabold text-4xl tracking-tight" data-testid="platform-form-title">EduKampus</h2>
                <p className="text-sm mt-1 text-white/60">
                  Sign in as super admin to manage every institute and campus on the platform.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="platform-login-form">
                <GhostBtn onClick={startGoogleSignIn} testid="platform-google-button">
                  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.79 2.74v2.28h2.9c1.7-1.56 2.69-3.87 2.69-6.66z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.28c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.71H.94v2.33A9 9 0 0 0 9 18z"/>
                    <path fill="#FBBC05" d="M3.95 10.69A5.4 5.4 0 0 1 3.66 9c0-.59.1-1.16.29-1.69V4.98H.94A9 9 0 0 0 0 9c0 1.45.35 2.82.94 4.02l3.01-2.33z"/>
                    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .94 4.98l3.01 2.33C4.66 5.18 6.65 3.58 9 3.58z"/>
                  </svg>
                  Sign in with Google
                </GhostBtn>

                <div className="relative my-3">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/10"></span></div>
                  <div className="relative flex justify-center text-[10px] font-mono uppercase tracking-[0.25em]">
                    <span className="px-2 bg-[#050510] text-white/50">or with email</span>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="platform-email" className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60">Platform Email</Label>
                  <Input id="platform-email" type="email" autoComplete="email" placeholder="you@example.com"
                    value={email} onChange={(e) => setEmail(e.target.value)} required
                    data-testid="platform-email-input" className="h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-white/40" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="platform-password" className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60">Password</Label>
                  <Input id="platform-password" type="password" autoComplete="current-password" placeholder="Enter your password"
                    value={password} onChange={(e) => setPassword(e.target.value)} required
                    data-testid="platform-password-input" className="h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-white/40" />
                </div>

                {error && errBox(error, "platform-error")}

                <PrimaryBtn type="submit" disabled={loading || !email || !password} testid="platform-signin-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                    <>Sign in to Platform <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>
                  )}
                </PrimaryBtn>

                <div className="text-center pt-1">
                  <Link
                    to="/forgot-password"
                    className="text-[11px] font-medium text-blue-400 hover:text-blue-300 underline underline-offset-4"
                    data-testid="platform-forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              </form>
            </>
          ) : (
            // ---- STAGE 2 — branded campus sign in ----
            <>
              <button
                type="button"
                onClick={() => { setStage("select"); setError(""); setEmail(""); setPassword(""); }}
                className="text-xs mb-4 flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"
                data-testid="login-back-button"
              >
                <ArrowLeft className="h-3 w-3" /> Pick a different institution / campus
              </button>

              <div className="mb-7">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2 text-blue-400">
                  Signing in to
                </div>
                <h2 className="font-display font-extrabold text-3xl tracking-tight" data-testid="stage2-form-title">
                  {ctx?.institution?.name || selectedInst?.name}
                </h2>
                <p className="text-sm mt-1 text-white/60" data-testid="stage2-form-campus">
                  {ctx?.campus?.name || selectedCampus?.name}{(ctx?.campus?.city || selectedCampus?.city) ? ` · ${ctx?.campus?.city || selectedCampus?.city}` : ""}
                </p>
              </div>

              <GhostBtn onClick={startGoogleSignIn} testid="login-google-button">
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.79 2.74v2.28h2.9c1.7-1.56 2.69-3.87 2.69-6.66z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.28c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.71H.94v2.33A9 9 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.95 10.69A5.4 5.4 0 0 1 3.66 9c0-.59.1-1.16.29-1.69V4.98H.94A9 9 0 0 0 0 9c0 1.45.35 2.82.94 4.02l3.01-2.33z"/>
                  <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .94 4.98l3.01 2.33C4.66 5.18 6.65 3.58 9 3.58z"/>
                </svg>
                Sign in with Google
              </GhostBtn>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/10"></span></div>
                <div className="relative flex justify-center text-[10px] font-mono uppercase tracking-[0.25em]">
                  <span className="px-2 bg-[#050510] text-white/50">or with email</span>
                </div>
              </div>

              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60">Work Email</Label>
                  <Input id="email" type="email" autoComplete="email" placeholder="you@institution.edu"
                    value={email} onChange={(e) => setEmail(e.target.value)} required
                    data-testid="login-email-input" className="h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-white/40" autoFocus />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60">Password</Label>
                    <Link to="/forgot-password" className="text-[11px] font-medium text-blue-400 hover:text-blue-300 underline underline-offset-4" data-testid="forgot-password-link">Forgot?</Link>
                  </div>
                  <Input id="password" type="password" autoComplete="current-password" placeholder="••••••••"
                    value={password} onChange={(e) => setPassword(e.target.value)} required
                    data-testid="login-password-input" className="h-11 bg-white/[0.03] border-white/10 text-white placeholder:text-white/40" />
                </div>

                {error && errBox(error, "login-error")}

                <PrimaryBtn type="submit" disabled={loading} testid="login-submit-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> :
                    <>Sign in <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>}
                </PrimaryBtn>

                <div className="text-[10px] font-mono uppercase tracking-[0.2em] pt-4 border-t border-white/10 text-white/50">
                  Forgot?{" "}
                  <Link to={`/forgot-password?institution_id=${institutionId}&campus_id=${campusId}`} className="text-blue-400 hover:text-blue-300 underline underline-offset-4">Reset password</Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
